import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Music, Play, Pause, RotateCw, Download, Sparkles, Share2, PlusCircle } from "lucide-react";
import { toast } from "sonner";

const MusicfulPage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [generatedMusic, setGeneratedMusic] = useState<string | null>(null);

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt) {
      toast.error("Por favor, insira uma descrição para gerar a música");
      return;
    }
    
    setIsGenerating(true);
    
    // Simulate music generation (mockado)
    setTimeout(() => {
      setGeneratedMusic("música_gerada_por_ia.mp3");
      setIsGenerating(false);
      toast.success("Música gerada com sucesso!");
    }, 3000);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    toast.info(isPlaying ? "Música pausada" : "Reproduzindo música");
  };

  const handleDownload = () => {
    toast.success("Música baixada com sucesso!");
  };

  const handleShare = () => {
    toast.success("Link de compartilhamento copiado para a área de transferência!");
  };

  const examplePrompts = [
    "Uma música eletrônica energética para uma festa",
    "Música ambiente relaxante com sons de natureza",
    "Uma trilha sonora épica para um filme de aventura",
    "Música jazz suave para um café da manhã",
    "Uma batida de hip hop com influências latinas"
  ];

  const templates = [
    { name: "Eletrônica", description: "Música eletrônica com batidas intensas" },
    { name: "Ambiente", description: "Sons relaxantes e atmosféricos" },
    { name: "Trilha Sonora", description: "Música cinematográfica e épica" },
    { name: "Lo-fi", description: "Batidas relaxantes com melodias suaves" },
    { name: "Pop", description: "Música pop cativante e animada" },
    { name: "Clássica", description: "Composições inspiradas na música clássica" }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Musicful.ai
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Crie músicas originais usando inteligência artificial. Transforme suas ideias em composições únicas com apenas algumas palavras.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Music className="mr-2 h-5 w-5" /> Gerar Música
              </CardTitle>
              <CardDescription>Descreva a música que você deseja criar</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerate} className="space-y-6">
                <div>
                  <Textarea 
                    placeholder="Descreva a música que você deseja criar. Ex: Uma música eletrônica energética com batidas fortes e melodia cativante..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="min-h-[120px]"
                  />
                  <div className="mt-2 text-xs text-muted-foreground">
                    Seja detalhado! Inclua gênero, ritmo, instrumentos, humor e qualquer outra característica desejada.
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    type="submit" 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground flex-grow"
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <RotateCw className="mr-2 h-4 w-4 animate-spin" /> Gerando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" /> Gerar Música
                      </>
                    )}
                  </Button>
                </div>
                
                {generatedMusic && (
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                      <div className="flex-grow">
                        <p className="text-sm text-muted-foreground mb-1">Música gerada:</p>
                        <div className="bg-background p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-10 w-10 rounded-full"
                              onClick={handlePlayPause}
                            >
                              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                            </Button>
                            <div className="ml-2">
                              <p className="font-medium">Música gerada com IA</p>
                              <p className="text-xs text-muted-foreground">01:30</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="icon" onClick={handleDownload}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="icon" onClick={handleShare}>
                              <Share2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Prompts de Exemplo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {examplePrompts.map((examplePrompt, index) => (
                  <Button 
                    key={index}
                    variant="outline" 
                    className="w-full justify-start text-left h-auto py-2"
                    onClick={() => setPrompt(examplePrompt)}
                  >
                    <PlusCircle className="mr-2 h-4 w-4 shrink-0" />
                    <span className="line-clamp-2">{examplePrompt}</span>
                  </Button>
                ))}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Acesse o Musicful.ai</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Acesse o site oficial do Musicful.ai para criar músicas com recursos completos e opções avançadas.
                </p>
                <Button 
                  asChild
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a 
                    href="https://www.musicful.ai/home/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Visitar Site Oficial
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <Tabs defaultValue="templates" className="mb-12">
          <TabsList className="grid grid-cols-2 md:w-[400px] mx-auto">
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="como-funciona">Como Funciona</TabsTrigger>
          </TabsList>
          
          <TabsContent value="templates" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Templates de Música</CardTitle>
                <CardDescription>Escolha um template para começar sua criação</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {templates.map((template, index) => (
                    <div 
                      key={index}
                      className="bg-muted p-4 rounded-lg cursor-pointer hover:bg-primary/10 transition-colors"
                      onClick={() => setPrompt(`Uma música ${template.name.toLowerCase()} com ${template.description.toLowerCase()}`)}
                    >
                      <h3 className="font-medium mb-1">{template.name}</h3>
                      <p className="text-xs text-muted-foreground">{template.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="como-funciona" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Como Funciona o Musicful.ai</CardTitle>
                <CardDescription>Entenda o processo de geração de música com IA</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <p className="text-muted-foreground">
                    O Musicful.ai utiliza modelos avançados de inteligência artificial para criar composições musicais originais
                    baseadas em descrições textuais. O processo funciona da seguinte forma:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                      <div className="bg-primary/10 p-3 rounded-full mb-4">
                        <span className="text-lg font-bold">1</span>
                      </div>
                      <h3 className="text-lg font-medium mb-2">Descrição</h3>
                      <p className="text-sm text-muted-foreground">Você descreve a música que deseja criar, incluindo gênero, humor, instrumentos e outros detalhes.</p>
                    </div>
                    
                    <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                      <div className="bg-primary/10 p-3 rounded-full mb-4">
                        <span className="text-lg font-bold">2</span>
                      </div>
                      <h3 className="text-lg font-medium mb-2">Processamento</h3>
                      <p className="text-sm text-muted-foreground">A IA analisa sua descrição e gera uma composição musical original que corresponde às características solicitadas.</p>
                    </div>
                    
                    <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                      <div className="bg-primary/10 p-3 rounded-full mb-4">
                        <span className="text-lg font-bold">3</span>
                      </div>
                      <h3 className="text-lg font-medium mb-2">Resultado</h3>
                      <p className="text-sm text-muted-foreground">Em segundos, você recebe uma música única e pronta para uso, que pode ser baixada em diferentes formatos.</p>
                    </div>
                  </div>
                  
                  <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
                    <h3 className="text-lg font-medium mb-2 text-center">Recursos do Musicful.ai</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <li className="flex items-center">
                        <Sparkles className="h-4 w-4 mr-2 text-primary" /> Geração de música em vários gêneros
                      </li>
                      <li className="flex items-center">
                        <Sparkles className="h-4 w-4 mr-2 text-primary" /> Controle de duração e estrutura
                      </li>
                      <li className="flex items-center">
                        <Sparkles className="h-4 w-4 mr-2 text-primary" /> Exportação em formatos de alta qualidade
                      </li>
                      <li className="flex items-center">
                        <Sparkles className="h-4 w-4 mr-2 text-primary" /> Licença para uso comercial
                      </li>
                      <li className="flex items-center">
                        <Sparkles className="h-4 w-4 mr-2 text-primary" /> Edição de parâmetros avançados
                      </li>
                      <li className="flex items-center">
                        <Sparkles className="h-4 w-4 mr-2 text-primary" /> Biblioteca de músicas salvas
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://www.musicful.ai/home/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Visitar Musicful.ai
            </a>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            Crie músicas incríveis e originais para seus projetos com a ajuda da inteligência artificial.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MusicfulPage;